/**
 * libp2p 浏览器节点（迁移自 `prototype/web/net/peer.js`，TS 化 + 新增 content 协议 client）。
 *
 * 与原型的关键差异：
 *  1. 帧编解码统一走 `@shared/protocol`（4 字节大端长度 + UTF-8 JSON），与 Node 服务**同源**
 *  2. 全部业务 handler 声明 `{ runOnTransientConnection: true }`（经中继的 circuit 连接也要跑）
 *  3. 新增 `/meshlink/content/1.0.0` 客户端（T04 的 `RelayContentStore` 直接用）
 *  4. 身份由 `peerSeed` 派生，PeerID 与 T02 的 `derivation` 结果**逐字节一致**（已单测交叉验证）
 */

import { createLibp2p } from 'libp2p';
import { webSockets } from '@libp2p/websockets';
import { noise } from '@chainsafe/libp2p-noise';
import { mplex } from '@libp2p/mplex';
import { identify } from '@libp2p/identify';
import { generateKeyPairFromSeed } from '@libp2p/crypto/keys';
import { createFromPrivKey } from '@libp2p/peer-id-factory';
import { peerIdFromString } from '@libp2p/peer-id';
import { multiaddr, type Multiaddr } from '@multiformats/multiaddr';

import {
  PROTOCOL,
  TRANSIENT_HANDLER_OPTS,
  encodeFrame,
  FrameDecoder,
  bytesToB64,
  b64ToBytes,
} from '@shared/protocol';
import type { ChatEnvelope, MailboxItem, RelayEntry } from '@shared/types';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';

const logger = createLogger('net:peer');

/** 各类请求的超时（ms）。 */
const TIMEOUT_SEND_MS = 10_000;
const TIMEOUT_POLL_MS = 8_000;
const TIMEOUT_DEFAULT_MS = 8_000;

/**
 * mplex `disconnectThreshold`：客户端侧「对端拿不存在的 stream 狂发消息」的 **DoS 护栏**，默认仅 5/秒。
 *
 * 为什么放宽到 200：实测 stray 帧**全部是合法拆解帧**（`CLOSE_RECEIVER` / `CLOSE_INITIATOR`），
 * **零数据帧**；来源是短命流「本端先拆、对端 CLOSE 后到」的**拆解竞态**，在启动 / 重连突发下必然
 * 发生（连 libp2p 内部 `/ipfs/id`、`/ipfs/ping` 流都会产生 → 我方代码无法消除）。默认 5 会被启动
 * 突发击穿 → mplex `abort('Too many messages for missing streams')` → 整条中继连接被断、触发重连抖动。
 * 实测峰值 6 条 / 326ms；与中继侧 `server/relay.ts` 的 200 对齐。护栏依旧有效（1s 内 >200 条才断）。
 */
const MPLEX_DISCONNECT_THRESHOLD = 200;

/**
 * WebSocket 拨号地址过滤器：放行末尾协议为 `ws` 或 `wss`、且不含 `p2p-circuit` 的多地址。
 *
 * 为什么必须显式传：`@libp2p/websockets` 在**浏览器环境默认只接受 DNS + `wss`**
 * （内置 `filters.wss`）。本项目的中继是 `/ip4/127.0.0.1/tcp/9101/ws`（本地明文），
 * 默认过滤器会在 `dialFilter` 阶段把它整条剔除，于是 libp2p 直接抛
 * `The dial request has no valid addresses` —— **一个包都不会发出去**，服务端也看不到任何连接。
 * 故在此传入自定义过滤器（等价于官方 `filters.all`），同时兼容 `ws` 与 `wss`。
 */
function wsOrWssFilter(multiaddrs: Multiaddr[]): Multiaddr[] {
  return multiaddrs.filter((ma) => {
    const s = ma.toString();
    return !s.includes('p2p-circuit') && /\/(ws|wss)(\/|$)/.test(s);
  });
}

/**
 * 连接 gater：放行私有 / 回环地址的拨号。
 *
 * `libp2p` 在**浏览器环境**内置的默认 gater 会用 `isPrivateIp()` 拒绝一切
 * 私网与回环地址（`config/connection-gater.browser.js`），本意是防止网页扫内网。
 * 但 MeshLink 是「本地优先 / 局域网优先」的 P2P 应用：中继就是
 * `/ip4/127.0.0.1/tcp/9101/ws`，直连目标也多为同网段设备。
 * 若不显式覆盖，拨号会在 gater 阶段被全部否决，报
 * `The connection gater denied all addresses in the dial request`，链路永远起不来。
 *
 * 因此这里选择放行（与 Node 端默认行为一致）。代价是页面具备访问内网的能力，
 * 属本应用的既定设计取舍，而非疏漏。
 */
const lanFirstGater = {
  denyDialMultiaddr: async (): Promise<boolean> => false,
};

/** libp2p 1.x 类型较复杂，这里只声明用到的最小面，避免与版本类型打架。 */
interface MinimalStream {
  source: AsyncIterable<Uint8Array | { subarray(): Uint8Array }>;
  sink(source: AsyncIterable<Uint8Array>): Promise<void>;
  close(): void | Promise<void>;
}

interface MinimalConnection {
  remotePeer: { toString(): string };
}

interface MinimalNode {
  peerId: { toString(): string };
  handle(protocol: string, handler: (arg: { stream: unknown; connection: MinimalConnection }) => void, opts?: unknown): Promise<void>;
  dialProtocol(peer: unknown, protocols: string | string[], opts?: unknown): Promise<MinimalStream>;
  dial(addr: unknown): Promise<MinimalConnection | undefined>;
  addEventListener(type: string, listener: (evt: { detail: MinimalConnection }) => void): void;
  stop(): Promise<void>;
}

/** 把分块字节统一成 Uint8Array。 */
function asBytes(value: Uint8Array | { subarray(): Uint8Array }): Uint8Array {
  return value instanceof Uint8Array ? value : value.subarray();
}

/** 中继侧下发的中继层消息（非聊天信封）。 */
export interface RelayWireMessage {
  type?: string;
  from?: string;
  to?: string;
  data?: string;
  ids?: string[];
  messages?: MailboxItem[];
  ok?: boolean;
  stored?: boolean;
  queued?: number;
  error?: string;
  peers?: unknown;
  total?: number;
  relays?: RelayEntry[];
  posts?: unknown;
  /** 群组（v3.9）：group_result 的 group、group_ack 的 delivered/stored。 */
  group?: unknown;
  groupId?: string;
  delivered?: number;
  /** 兼容旧写法：单条帖子（当前服务端已统一为 `posts` 数组）。 */
  post?: unknown;
  bytesB64?: string;
  mime?: string;
  /** 评论（v3.6.4）：历史列表 / 单条广播。 */
  comments?: unknown;
  postCid?: string;
  comment?: unknown;
}

export interface PeerClientOptions {
  /** 32B Ed25519 私钥种子（peer 分域），用于生成与 T02 一致的 PeerID。 */
  peerSeed: Uint8Array;
  /** 收到聊天信封（含 `kind:'signal'` 的 WebRTC 信令）。 */
  onEnvelope?(env: ChatEnvelope, fromPeerId: string, source?: 'live' | 'mailbox'): void;
  /** 收到广场新帖（T04）。 */
  onFeedPost?(post: unknown): void;
  /** 连接变化（供上层触发重连）。 */
  onConnectionChange?(kind: 'connected' | 'disconnected', peerId: string): void;
}

/** 浏览器端 libp2p 节点客户端。 */
export class Libp2pBrowserPeer {
  private node: MinimalNode | null = null;
  private relayPeerId: string | null = null;
  private relayAddr: string | null = null;
  private feedStream: MinimalStream | null = null;
  /** 评论订阅长连接（postCid → 订阅流）。 */
  private commentStreams = new Map<string, MinimalStream>();
  /** 群订阅流：groupId → 常驻订阅流（v3.9）。 */
  private groupStreams = new Map<string, MinimalStream>();
  private started = false;
  private feedCallback: ((posts: unknown) => void) | null = null;

  constructor(private readonly opts: PeerClientOptions) {}

  get peerId(): string {
    return this.node?.peerId.toString() ?? '';
  }

  getRelayPeerId(): string | null {
    return this.relayPeerId;
  }

  getRelayAddr(): string | null {
    return this.relayAddr;
  }

  /** 注册广场实时新帖回调（FeedService 调用）。 */
  setFeedCallback(cb: (posts: unknown) => void): void {
    this.feedCallback = cb;
  }

  /** 分发新帖：优先走注入回调，再走构造期 onFeedPost（兼容测试）。 */
  private dispatchFeed(posts: unknown): void {
    this.feedCallback?.(posts);
    this.opts.onFeedPost?.(posts);
  }

  isStarted(): boolean {
    return this.started;
  }

  /** 创建节点并注册全部 handler。 */
  async start(): Promise<string> {
    if (this.started) return this.peerId;

    const keyPair = await generateKeyPairFromSeed('Ed25519', this.opts.peerSeed);
    const peerId = await createFromPrivKey(keyPair);

    const node = (await createLibp2p({
      peerId,
      addresses: { listen: [] },
      transports: [webSockets({ filter: wsOrWssFilter })],
      connectionEncryption: [noise()],
      streamMuxers: [mplex({ maxInboundStreams: 512, maxOutboundStreams: 512, disconnectThreshold: MPLEX_DISCONNECT_THRESHOLD })],
      services: { identify: identify() },
      connectionGater: lanFirstGater,
    })) as unknown as MinimalNode;

    this.node = node;
    await this.registerHandlers(node);

    node.addEventListener('connection:open', (evt) => {
      const pid = evt.detail.remotePeer.toString();
      logger.debug('连接打开', pid.slice(0, 12));
      this.opts.onConnectionChange?.('connected', pid);
    });
    node.addEventListener('connection:close', (evt) => {
      const pid = evt.detail.remotePeer.toString();
      logger.debug('连接关闭', pid.slice(0, 12));
      this.opts.onConnectionChange?.('disconnected', pid);
    });

    this.started = true;
    logger.info('libp2p 节点已启动', this.peerId.slice(0, 16));
    return this.peerId;
  }

  // ─────────────── handler 注册 ───────────────

  private async registerHandlers(node: MinimalNode): Promise<void> {
    // ① 中继投递 / 邮箱推送
    await node.handle(
      PROTOCOL.RELAY_MESSAGE,
      ({ stream, connection }) => {
        const fromPeer = connection.remotePeer.toString();
        const minimal = stream as unknown as MinimalStream;
        void this.readLoop(minimal, async (msg) => {
          if (msg.type === 'deliver' && msg.data) {
            await this.dispatchBase64(msg.data, msg.from ?? fromPeer, 'live');
            await this.writeFrame(minimal, { type: 'deliver_received' }).catch(() => undefined);
          } else if (msg.type === 'mailbox' && Array.isArray(msg.messages)) {
            const ids: string[] = [];
            for (const item of msg.messages) {
              ids.push(item.id);
              try {
                await this.dispatchBase64(item.data, item.from, 'mailbox');
              } catch (err) {
                logger.warn('邮箱消息处理失败', err instanceof Error ? err.message : err);
              }
            }
            await this.writeFrame(minimal, { type: 'mailbox_received', ids }).catch(() => undefined);
          }
        });
      },
      TRANSIENT_HANDLER_OPTS,
    );

    // ② 广场实时推送（T04）
    await node.handle(
      PROTOCOL.FEED,
      ({ stream }) => {
        const minimal = stream as unknown as MinimalStream;
        void this.readLoop(minimal, async (msg) => {
          if (msg.type !== 'feed_post') return;
          const posts = this.readFeedFrame(msg);
          if (posts) this.dispatchFeed(posts);
        });
      },
      TRANSIENT_HANDLER_OPTS,
    );
  }

  /**
   * 从广场帧里取出帖子数组。
   * 兼容两种历史写法：`{posts:[…]}`（当前服务端 / history_result 命名）与 `{post:…}`（旧写法）。
   */
  private readFeedFrame(msg: RelayWireMessage): unknown[] | null {
    if (Array.isArray(msg.posts)) return msg.posts;
    if (msg.post !== undefined && msg.post !== null) return [msg.post];
    return null;
  }

  /** 从 base64 帧串解出聊天信封并逐个派发。source 区分实时投递与离线邮箱补收。 */
  private async dispatchBase64(dataB64: string, fromPeerId: string, source: 'live' | 'mailbox' = 'live'): Promise<void> {
    const frames = FrameDecoder.fromBase64(dataB64);
    for (const frame of frames) {
      const env = frame as ChatEnvelope;
      if (!env || typeof env !== 'object' || env.protocol !== 'meshlink/chat/1.0') continue;
      try {
        this.opts.onEnvelope?.(env, fromPeerId, source);
      } catch (err) {
        logger.error('信封派发失败', err instanceof Error ? err.message : err);
      }
    }
  }

  private async readLoop(stream: MinimalStream, onMsg: (msg: RelayWireMessage) => Promise<void>): Promise<void> {
    const decoder = new FrameDecoder();
    try {
      for await (const value of stream.source) {
        for (const frame of decoder.push(asBytes(value))) {
          await onMsg(frame as RelayWireMessage);
        }
      }
    } catch (err) {
      if (!isBenignStreamError(err)) {
        logger.warn('流读取中断', err instanceof Error ? err.message : err);
      }
    }
  }

  private async writeFrame(stream: MinimalStream, obj: unknown): Promise<void> {
    const frame = encodeFrame(obj);
    await stream.sink(
      (async function* () {
        yield frame;
      })(),
    );
  }

  // ─────────────── 中继请求 ───────────────

  /** 向中继发一帧并等待指定类型的响应帧。 */
  private async request(
    protocol: string,
    req: Record<string, unknown>,
    expectType: string | null,
    timeoutMs = TIMEOUT_DEFAULT_MS,
  ): Promise<RelayWireMessage> {
    const node = this.requireNode();
    if (!this.relayPeerId) throw new MeshLinkError('E_RELAY_UNAVAILABLE', '尚未连接中继');

    const stream = await node.dialProtocol(peerIdFromString(this.relayPeerId), protocol, TRANSIENT_HANDLER_OPTS);
    await this.writeFrame(stream, req);

    const decoder = new FrameDecoder();
    const deadline = Date.now() + timeoutMs;
    for await (const value of stream.source) {
      for (const frame of decoder.push(asBytes(value))) {
        const msg = frame as RelayWireMessage;
        if (!expectType || msg.type === expectType) return msg;
      }
      if (Date.now() > deadline) break;
    }
    throw new MeshLinkError('E_RELAY_UNAVAILABLE', '中继无响应');
  }

  /**
   * 单向通知：写一帧后**等待对端关闭流**（= 已处理），不期待回帧、不抛错。
   *
   * 用于 `mailbox_received` 这类「服务端只收不回」的确认帧（见 `server/relay.ts:441`）。
   * 若改用 `request(..., null)`：服务端不回帧，`request` 会在流关闭后抛 `E_RELAY_UNAVAILABLE`，
   * 调用方只能 `.catch(() => undefined)` 吞掉 —— 每轮轮询都会产生一个伪错误。
   * 语义不变（仍然等对端处理完并拆除流，保证服务端确实已 `mailboxTake`），但错误路径变诚实。
   */
  private async notify(protocol: string, req: Record<string, unknown>, timeoutMs = TIMEOUT_DEFAULT_MS): Promise<void> {
    const node = this.requireNode();
    if (!this.relayPeerId) throw new MeshLinkError('E_RELAY_UNAVAILABLE', '尚未连接中继');
    const stream = await node.dialProtocol(peerIdFromString(this.relayPeerId), protocol, TRANSIENT_HANDLER_OPTS);
    await this.writeFrame(stream, req);
    const deadline = Date.now() + timeoutMs;
    let ignored = 0;
    try {
      // 服务端不回帧；若有内容也一律忽略，只需等它把流关掉
      for await (const value of stream.source) {
        ignored += asBytes(value).length;
        if (Date.now() > deadline) break;
      }
    } catch {
      // 对端拆解竞态导致的读中断属常态
    }
    if (ignored > 0) logger.debug('notify 收到预期外的回帧（已忽略）', ignored);
  }

  private async requestWithTimeout<T extends RelayWireMessage>(
    protocol: string,
    req: Record<string, unknown>,
    expectType: string | null,
    timeoutMs: number,
  ): Promise<T> {
    const result = await Promise.race([
      this.request(protocol, req, expectType, timeoutMs),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new MeshLinkError('E_RELAY_UNAVAILABLE', '中继响应超时')), timeoutMs + 500),
      ),
    ]);
    return result as T;
  }

  // ─────────────── 对外能力 ───────────────

  /**
   * 拨号中继。`addr` 可为多地址或 ws(s):// URL。
   *
   * 地址可能**不带** `/p2p/<peerId>`：`/relay-info` 发现失败时 `resolveRelayAddr()` 回落到内置默认
   * 地址（无后缀），或 `.env.local` 只配了 `VITE_RELAY_ADDR` 而留空 `VITE_RELAY_PEER_ID`。
   * 此时必须从**拨号结果**反查真实 PeerID —— 否则 `relayPeerId` 恒 null ⇒ 所有中继请求抛
   * `E_RELAY_UNAVAILABLE`、`feedSubscribe`/`pollMailbox` 静默失效、与中继断开后**永不重连**
   * （即「UI 绿灯却发不出消息」的假就绪）。
   *
   * 注：`libp2p@1.9.4` 仅在「过滤后无可用地址」时才抛 `The dial request has no valid addresses`；
   * 裸地址（ws 传输可用、gater 放行）会拨号成功，`dial()` resolve 出的 `Connection.remotePeer`
   * 即真实中继 PeerID，故对端身份以握手结果为准（地址是否携带 `/p2p/` 不影响能否拨通）。
   */
  async connectRelay(addr: string): Promise<boolean> {
    const node = this.requireNode();
    const match = addr.match(/\/p2p\/([A-Za-z0-9]+)/);
    if (match) this.relayPeerId = match[1];
    const ma = toMultiaddr(addr);
    logger.info('拨号中继', ma);
    const conn = await node.dial(multiaddr(ma));
    // 对端身份以握手结果为准：地址未携带 /p2p/（或携带的与真实身份不符）时从中继连接反查回填。
    const learned = conn?.remotePeer?.toString();
    if (learned) {
      if (!match) logger.info('从连接反查中继 PeerID', learned.slice(0, 16));
      this.relayPeerId = learned;
    }
    this.relayAddr = addr;
    // 给 identify 一点时间，避免立刻发业务请求时拿不到对方协议表
    await new Promise((r) => setTimeout(r, 300));
    return true;
  }

  /**
   * 发送聊天信封（中继转发；对方离线自动入邮箱）。
   * @returns `{ stored, queued }` —— stored=true 表示已入离线邮箱
   */
  async sendMessage(toPeerId: string, envelope: ChatEnvelope): Promise<{ stored: boolean; queued: number }> {
    const data = bytesToB64(encodeFrame(envelope));
    const ack = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.RELAY_MESSAGE,
      { type: 'send', to: toPeerId, data },
      'ack',
      TIMEOUT_SEND_MS,
    );
    if (!ack.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', ack.error ?? '中继转发失败');
    return { stored: ack.stored === true, queued: ack.queued ?? 0 };
  }

  /**
   * 主动拉取离线邮箱（上线首次 + 每 30s 兜底）。
   * 拉取成功后立即回 `mailbox_received`，服务端据此删除条目（原型缺陷修复点）。
   * @returns 本次解密前收到的信封条数
   */
  async pollMailbox(): Promise<number> {
    if (!this.relayPeerId) return 0;
    let count = 0;
    try {
      const res = await this.requestWithTimeout<RelayWireMessage>(
        PROTOCOL.RELAY_MESSAGE,
        { type: 'mailbox_poll' },
        'mailbox',
        TIMEOUT_POLL_MS,
      );
      const items = Array.isArray(res.messages) ? res.messages : [];
      const ids: string[] = [];
      for (const item of items) {
        ids.push(item.id);
        try {
          await this.dispatchBase64(item.data, item.from, 'mailbox');
          count += 1;
        } catch (err) {
          logger.warn('邮箱信封处理失败', err instanceof Error ? err.message : err);
        }
      }
      if (ids.length > 0) {
        await this.notify(PROTOCOL.RELAY_MESSAGE, { type: 'mailbox_received', ids });
      }
    } catch (err) {
      // 无邮箱 / 超时属常态，不上抛，避免打断轮询节奏
      logger.debug('邮箱轮询无结果', err instanceof Error ? err.message : err);
    }
    return count;
  }

  /** 上报在线可见性（隐身为 false）。 */
  async setVisible(visible: boolean, name: string): Promise<void> {
    if (!this.relayPeerId) throw new MeshLinkError('E_RELAY_UNAVAILABLE', '尚未连接中继');
    await this.request(PROTOCOL.DIRECTORY, { type: 'status', visible, name }, 'status_ack').catch(() => undefined);
  }

  /** 拉取在线用户列表（服务端已过滤隐身者与自己）。 */
  async getOnlinePeers(): Promise<{ peers: Array<{ peerId: string; name: string; connectedAt: number }>; total: number }> {
    const res = await this.request(PROTOCOL.DIRECTORY, { type: 'list' }, 'list_result');
    const peers = (Array.isArray(res.peers) ? res.peers : []) as Array<{ peerId: string; name: string; connectedAt: number }>;
    return { peers, total: res.total ?? peers.length };
  }

  /** 拉取桌面中继目录。 */
  async getRelayDirectory(): Promise<RelayEntry[]> {
    if (!this.relayPeerId) return [];
    const res = await this.request(PROTOCOL.RELAY_DIRECTORY, { type: 'list' }, 'relays');
    return Array.isArray(res.relays) ? res.relays : [];
  }

  // ─────────────── 广场（T04 用，T03 先备好通道） ───────────────

  /** 长连接订阅实时新帖（幂等：先关旧流）。 */
  feedSubscribe(): void {
    if (!this.relayPeerId) return;
    if (this.feedStream) {
      try {
        this.feedStream.close();
      } catch {
        /* noop */
      }
      this.feedStream = null;
    }
    void (async () => {
      const node = this.requireNode();
      const stream = await node.dialProtocol(
        peerIdFromString(this.relayPeerId as string),
        PROTOCOL.FEED,
        TRANSIENT_HANDLER_OPTS,
      );
      this.feedStream = stream;
      await this.readLoop(stream, async (msg) => {
        if (msg.type !== 'feed_post') return;
        const posts = this.readFeedFrame(msg);
        // 必须走 dispatchFeed：FeedService 的回调存在 `feedCallback`（经 setFeedCallback 注入），
        // 而 `onFeedPost` 构造期并未传入 → 只调 onFeedPost 会导致实时广场推送完全失效（T03 缺陷2）。
        if (posts) this.dispatchFeed(posts);
      });
    })().catch((err) => {
      if (!isBenignStreamError(err)) logger.warn('广场订阅断开', err instanceof Error ? err.message : err);
    });
  }

  /** 拉取广场历史帖子（请求/应答 → 走 FEED_REQ 短命流，勿用 FEED 订阅协议）。 */
  async feedHistory(): Promise<unknown[]> {
    const res = await this.request(PROTOCOL.FEED_REQ, { type: 'history' }, 'history_result');
    return Array.isArray(res.posts) ? res.posts : [];
  }

  /** 发布帖子（只传 Manifest，不含图片字节；请求/应答 → 走 FEED_REQ 短命流）。 */
  async feedPublish(post: unknown): Promise<unknown> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.FEED_REQ,
      { type: 'publish', post },
      'publish_ack',
      12_000,
    );
    if (!res.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', res.error ?? '发布失败');
    return res;
  }

  // ─────────────── 评论（v3.6.4：P2P 广播 + 按帖历史 + 实时订阅） ───────────────

  /** 拉取某帖评论（请求/应答 → 走 COMMENT_REQ 短命流）。 */
  async commentHistory(postCid: string): Promise<unknown[]> {
    const res = await this.request(
      PROTOCOL.COMMENT_REQ,
      { type: 'history', postCid },
      'history_result',
    );
    return Array.isArray(res.comments) ? res.comments : [];
  }

  /** 发布一条评论（请求/应答 → 走 COMMENT_REQ 短命流）。 */
  async commentPublish(postCid: string, comment: unknown): Promise<unknown> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.COMMENT_REQ,
      { type: 'publish', postCid, comment },
      'publish_ack',
      12_000,
    );
    if (!res.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', res.error ?? '评论发送失败');
    return res;
  }

  /**
   * 订阅某帖评论实时流（打开评论面板时调用；幂等：同帖已有则先关旧流）。
   * 回调收到的是中继广播的 `comment_post` 帧内的 comment 对象。
   */
  commentSubscribe(postCid: string, cb: (comment: unknown) => void): void {
    if (!this.relayPeerId) return;
    this.commentUnsubscribe(postCid);
    void (async () => {
      const node = this.requireNode();
      const stream = await node.dialProtocol(
        peerIdFromString(this.relayPeerId as string),
        PROTOCOL.COMMENT,
        TRANSIENT_HANDLER_OPTS,
      );
      this.commentStreams.set(postCid, stream);
      await stream.sink(
        (async function* () {
          yield encodeFrame({ type: 'subscribe', postCid });
        })(),
      );
      await this.readLoop(stream, async (msg) => {
        if (msg.type !== 'comment_post') return;
        if (msg.postCid !== postCid) return;
        if (msg.comment != null) cb(msg.comment);
      });
    })().catch((err) => {
      if (!isBenignStreamError(err)) logger.warn('评论订阅断开', err instanceof Error ? err.message : err);
    });
  }

  /** 取消某帖评论订阅（关闭评论面板时调用）。 */
  commentUnsubscribe(postCid: string): void {
    const stream = this.commentStreams.get(postCid);
    if (stream) {
      try {
        stream.close();
      } catch {
        /* noop */
      }
      this.commentStreams.delete(postCid);
    }
  }

  // ─────────────── 群组（v3.9：GROUP/GROUP_REQ，与评论同构） ───────────────

  /** 创建群（请求/应答 → 走 GROUP_REQ 短命流）。 */
  async groupCreate(name: string, creator: string): Promise<unknown> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.GROUP_REQ,
      { type: 'create', name, creator },
      'group_result',
      12_000,
    );
    if (!res.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', (res as { error?: string }).error ?? '建群失败');
    return res.group;
  }

  /** 邀请成员入群（请求/应答）。 */
  async groupInvite(groupId: string, peerIds: string[]): Promise<unknown> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.GROUP_REQ,
      { type: 'invite', groupId, peerIds },
      'group_result',
      12_000,
    );
    if (!res.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', (res as { error?: string }).error ?? '邀请失败');
    return res.group;
  }

  /** 退出群（请求/应答）。 */
  async groupLeave(groupId: string, peerId: string): Promise<unknown> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.GROUP_REQ,
      { type: 'leave', groupId, peerId },
      'group_result',
      12_000,
    );
    if (!res.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', (res as { error?: string }).error ?? '退群失败');
    return res.group;
  }

  /** 拉取群成员（请求/应答）。 */
  async groupMembers(groupId: string): Promise<unknown> {
    const res = await this.request(PROTOCOL.GROUP_REQ, { type: 'members', groupId }, 'group_result');
    if (!res.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', (res as { error?: string }).error ?? '拉群失败');
    return res.group;
  }

  /**
   * 发布群消息：envelopes = 逐成员加密的信封 base64 列表（端到端，服务端只按 to 路由）。
   * 请求/应答 → 走 GROUP_REQ 短命流。
   */
  async groupPublish(groupId: string, from: string, envelopes: Array<{ to: string; data: string }>): Promise<unknown> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.GROUP_REQ,
      { type: 'publish', groupId, from, envelopes },
      'group_ack',
      20_000,
    );
    if (!res.ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', (res as { error?: string }).error ?? '群消息发送失败');
    return res;
  }

  /**
   * 订阅某群实时事件（成员变化广播 group_event；幂等：同群已有则先关旧流）。
   * 群消息本身不经此流——逐成员信封走 RELAY_MESSAGE deliver。
   */
  groupSubscribe(groupId: string, cb: (msg: { type: string; group?: unknown }) => void): void {
    if (!this.relayPeerId) return;
    this.groupUnsubscribe(groupId);
    void (async () => {
      const node = this.requireNode();
      const stream = await node.dialProtocol(
        peerIdFromString(this.relayPeerId as string),
        PROTOCOL.GROUP,
        TRANSIENT_HANDLER_OPTS,
      );
      this.groupStreams.set(groupId, stream);
      await stream.sink(
        (async function* () {
          yield encodeFrame({ type: 'subscribe', groupId });
        })(),
      );
      await this.readLoop(stream, async (msg) => {
        if (msg.type !== 'group_event') return;
        if (msg.groupId !== groupId) return;
        if (msg.group != null) cb({ type: 'group_event', group: msg.group });
      });
    })().catch((err) => {
      if (!isBenignStreamError(err)) logger.warn('群订阅断开', err instanceof Error ? err.message : err);
    });
  }

  /** 取消某群订阅。 */
  groupUnsubscribe(groupId: string): void {
    const stream = this.groupStreams.get(groupId);
    if (stream) {
      try {
        stream.close();
      } catch {
        /* noop */
      }
      this.groupStreams.delete(groupId);
    }
  }

  // ─────────────── 内容协议 client（T04 的 RelayContentStore 直接用） ───────────────

  /** 上传 blob。 */
  async contentPut(cid: string, mime: string, bytes: Uint8Array): Promise<boolean> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.CONTENT,
      { type: 'put', cid, mime, bytesB64: bytesToB64(bytes) },
      'put_ack',
      15_000,
    );
    return res.ok === true;
  }

  /** 下载 blob。 */
  async contentGet(cid: string): Promise<{ bytes: Uint8Array; mime: string } | null> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.CONTENT,
      { type: 'get', cid },
      'get_result',
      15_000,
    );
    if (!res.ok || !res.bytesB64) return null;
    return { bytes: b64ToBytes(res.bytesB64), mime: res.mime ?? 'application/octet-stream' };
  }

  /** 探测 blob 是否存在。 */
  async contentHas(cid: string): Promise<boolean> {
    const res = await this.requestWithTimeout<RelayWireMessage>(
      PROTOCOL.CONTENT,
      { type: 'has', cid },
      'has_result',
      8_000,
    );
    return res.ok === true;
  }

  // ─────────────── 生命周期 ───────────────

  private requireNode(): MinimalNode {
    if (!this.node) throw new MeshLinkError('E_RELAY_UNAVAILABLE', 'libp2p 节点尚未启动');
    return this.node;
  }

  async stop(): Promise<void> {
    if (this.feedStream) {
      try {
        this.feedStream.close();
      } catch {
        /* noop */
      }
      this.feedStream = null;
    }
    for (const [postCid, stream] of this.commentStreams) {
      try {
        stream.close();
      } catch {
        /* noop */
      }
      this.commentStreams.delete(postCid);
    }
    if (this.node) {
      await this.node.stop().catch(() => undefined);
      this.node = null;
    }
    this.relayPeerId = null;
    this.relayAddr = null;
    this.started = false;
    logger.info('libp2p 节点已停止');
  }
}

/** 断流类错误（重置 / 中止）不算故障，不刷日志。 */
export function isBenignStreamError(err: unknown): boolean {
  const e = err as { code?: string; type?: string } | null;
  if (!e) return false;
  return e.code === 'ERR_STREAM_RESET' || e.type === 'aborted' || e.code === 'ABORT_ERR';
}

/**
 * 地址 → 多地址。
 * 支持：`/ip4/…`（原样）、`ws(s)://host:port[/p2p/ID]`。
 */
export function toMultiaddr(addr: string): string {
  const trimmed = addr.trim();
  if (trimmed.startsWith('/')) return trimmed;
  try {
    const u = new URL(trimmed);
    const host = u.hostname;
    const port = u.port || (u.protocol === 'wss:' ? '443' : '80');
    const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(host);
    const proto = u.protocol === 'wss:' ? 'wss' : 'ws';
    const addrType = isIp ? 'ip4' : 'dns4';
    const path = u.pathname && u.pathname !== '/' ? u.pathname : '';
    return `/${addrType}/${host}/tcp/${port}/${proto}${path}`;
  } catch {
    return trimmed;
  }
}

export default Libp2pBrowserPeer;
